import type { Porocilo, VrsticaPorocila } from '@koda/shared';
import type { JSX } from 'react';

import { jeNegativen, odstotek, znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import type { Celica } from './PanelRazclenitve';

interface Lastnosti {
  tabela: Porocilo;
  /** Klik na znesek odpre razčlenitev te celice. */
  naCelico: (celica: Celica) => void;
  /** Vrstica odprte razčlenitve; v tabeli je označena. */
  izbrana?: VrsticaPorocila | null;
}

/**
 * Tabela zavihkov Partnerji in RVC po kupcih (docs/ARHITEKTURA.md, 6.5).
 *
 * Vrstice so partnerji, razvrščeni padajoče, nato »Ostali« in skupna vrstica.
 * Stolpci so mere (znesek, delež, lani, sprememba …), ne meseci, zato tabela
 * nima stolpca »skupaj«. Kateri stolpec je odstotek, pove `enota` stolpca.
 *
 * Klikljiva je samo številka, ki je vsota knjižb: to pove `filtriStolpcev`
 * vrstice (prazen seznam pri deležu in spremembi). Lani je vsota v drugem
 * letu, zato razčlenitev dobi svoj filter in ne filtra vrstice.
 * Delež je poleg številke še kot vodoraven trak, da se razmerja vidijo brez
 * branja številk.
 */
export function TabelaPartnerjev({ tabela, naCelico, izbrana = null }: Lastnosti): JSX.Element {
  const b = useBesedila();
  /**
   * Stolpec deleža je tisti odstotni stolpec, ki se v skupni vrstici sešteje
   * v 100 (marža in sprememba se ne). Tako tabela ne pozna vrstnega reda
   * stolpcev strežnika.
   */
  const jeDelez = (i: number): boolean => tabela.stolpci[i]?.enota === 'odstotek' && Number(tabela.skupaj?.zneski[i] ?? '') === 100;
  const obdobje = (v: VrsticaPorocila): { obdobjeOd: number; obdobjeDo: number } => ({
    obdobjeOd: v.filter.obdobjeOd,
    obdobjeDo: v.filter.obdobjeDo,
  });

  const celica = (vrstica: VrsticaPorocila, i: number): Celica => {
    const stolpec = tabela.stolpci[i]!;
    const filtri = vrstica.filtriStolpcev?.[i] ?? [];
    const z = vrstica.zneski[i] ?? '';
    return {
      vrstica,
      ...obdobje(vrstica),
      naslovObdobja: stolpec.naziv,
      znesek: z,
      // Vsaka številka nosi svoj filter (lani je drugo leto); en del, zato
      // panel naslova dela ne ponavlja.
      deli: filtri.map((f) => ({ naslov: stolpec.naziv, filter: f, znesek: z })),
    };
  };

  const stevilka = (vrstica: VrsticaPorocila, i: number): JSX.Element => {
    const stolpec = tabela.stolpci[i]!;
    const z = vrstica.zneski[i] ?? '';
    const jeOdstotek = stolpec.enota === 'odstotek';
    const razred = ['stevilka', jeOdstotek ? 'odstotek' : '', jeNegativen(z) ? 'negativen' : ''].filter((r) => r !== '').join(' ');
    if (z === '') return <td key={stolpec.obdobje} className={razred} />;
    if (jeOdstotek) {
      // Delež glede na vsoto: trak dolžine deleža poleg številke.
      const delez = Math.max(0, Math.min(100, Number(z)));
      return (
        <td key={stolpec.obdobje} className={razred}>
          {jeDelez(i) ? (
            <span className="delez-trak">
              <span className="trak" style={{ width: `${String(delez)}%` }} aria-hidden="true" />
              <span className="odstotek-besedilo">{odstotek(z)}</span>
            </span>
          ) : (
            odstotek(z)
          )}
        </td>
      );
    }
    const klikljiva = vrstica.klikljiva && (vrstica.filtriStolpcev?.[i]?.length ?? 0) > 0;
    if (!klikljiva) {
      return (
        <td key={stolpec.obdobje} className={razred}>
          <span className={jeNegativen(z) ? 'znesek negativen' : 'znesek'}>{znesek(z)}</span>
        </td>
      );
    }
    return (
      <td key={stolpec.obdobje} className={razred}>
        <button
          type="button"
          className={jeNegativen(z) ? 'znesek negativen' : 'znesek'}
          onClick={() => {
            naCelico(celica(vrstica, i));
          }}
          title={b.namigCelice}
        >
          {znesek(z)}
        </button>
      </td>
    );
  };

  const vrsta = (vrstica: VrsticaPorocila, mesto: number | null): JSX.Element => (
    <tr
      key={vrstica.kljuc}
      className={`${vrstica.vrstaVrstice ?? 'postavka'}${vrstica === izbrana ? ' izbrana' : ''}`}
    >
      <td className="mesto">{mesto === null ? '' : String(mesto)}</td>
      <th className="naziv" scope="row">
        <span>{vrstica.naziv}</span>
      </th>
      {tabela.stolpci.map((_, i) => stevilka(vrstica, i))}
    </tr>
  );

  return (
    <div className="okvir-primerjave">
      <table className="porocilo partnerji">
        <thead>
          <tr>
            <th className="mesto" aria-label={b.pa.mesto} />
            <th className="naziv">{b.pa.partner}</th>
            {tabela.stolpci.map((s) => (
              <th key={s.obdobje} className={s.enota === 'odstotek' ? 'stevilka odstotek' : 'stevilka'}>
                {s.naziv}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tabela.vrstice.length === 0 ? (
            <tr>
              <td colSpan={2 + tabela.stolpci.length}>{b.pa.niPartnerjev}</td>
            </tr>
          ) : (
            <>
              {tabela.vrstice.map((v, i) => vrsta(v, v.kljuc === 'ostali' ? null : i + 1))}
              {tabela.skupaj !== null && vrsta(tabela.skupaj, null)}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
